import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { BrandMark } from "@/components/Nav";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success("Account created.");
      nav("/", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 relative">
      <div className="hs-noise" />

      <div
        className="relative hidden md:flex flex-col justify-between p-12 border-r border-hs overflow-hidden"
        style={{ background: "var(--hs-surface)" }}
      >
        <Link to="/" className="flex items-center gap-3 relative z-10">
          <BrandMark />
          <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-muted-hs pl-3 border-l border-hs">
            Development Review
          </span>
        </Link>

        <div className="absolute inset-0 hs-grid-bg opacity-30" />

        <div className="relative z-10">
          <div className="hs-chip mb-8"><span className="dot" /> AUTH · NEW ACCOUNT</div>
          <h2 className="font-display text-5xl leading-[0.98] mb-6">
            Request <span className="text-warm italic">access.</span>
          </h2>
          <p className="text-muted-hs max-w-md leading-relaxed">
            The Harsight portal is invite-only for hospital finance teams and partners of HARS Advisors.
            Create your account and we'll provision entitlements to the applications on file.
          </p>
        </div>

        <div className="relative z-10 font-mono text-[10px] uppercase tracking-[0.24em] text-muted-hs">
          [ FIG_REGISTER · HIPAA_ALIGNED ]
        </div>
      </div>

      <div className="relative flex flex-col justify-between p-8 md:p-16" style={{ background: "var(--hs-bg)" }}>
        <div className="flex justify-between items-center">
          <Link to="/" className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-hs hs-link inline-flex items-center gap-2">
            <ArrowLeft size={14} /> Back
          </Link>
          <Link to="/login" className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-hs hs-link" data-testid="register-link-login">
            Have an account? →
          </Link>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-md w-full mx-auto"
        >
          <span className="hs-chip mb-8"><span className="dot" /> STEP 01 · CREATE</span>
          <h1 className="font-display text-5xl md:text-6xl leading-[0.95] mb-3">Create account.</h1>
          <p className="text-muted-hs mb-12">A single sign-in for the entire Harsight product family.</p>

          <form onSubmit={submit} className="space-y-8" data-testid="register-form">
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-hs">Full name</span>
              <input required value={name} onChange={(e) => setName(e.target.value)} className="hs-input" data-testid="register-input-name" />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-hs">Work email</span>
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="hs-input" data-testid="register-input-email" />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-hs">Password (min 6 chars)</span>
              <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="hs-input" data-testid="register-input-password" />
            </label>

            {error && (
              <div className="font-mono text-xs uppercase tracking-[0.15em] text-warm border border-warm/40 px-4 py-3" data-testid="register-error">
                {error}
              </div>
            )}

            <button type="submit" disabled={loading} className="hs-btn hs-btn-primary w-full justify-center" data-testid="register-submit">
              {loading ? "Creating…" : "Create account"} <ArrowRight size={14} />
            </button>
          </form>
        </motion.div>

        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-hs">
          © {new Date().getFullYear()} Harsight
        </div>
      </div>
    </div>
  );
}
